import { Link } from "react-router-dom";
import PageMeta from "@/components/base/PageMeta";
import SiteLayout from "@/components/feature/SiteLayout";
import SectionHeading from "@/components/feature/SectionHeading";
import ComicCard from "@/components/feature/ComicCard";
import ComicRow from "@/components/feature/ComicRow";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import CategoryQuickLinks from "@/pages/home/components/CategoryQuickLinks";
import HotRanking from "@/pages/home/components/HotRanking";
import { latestComics, popularComics, tagOptions, allComics, allCategories } from "@/utils/comics";

export default function Home() {
  const latest = latestComics(6);
  const popular = popularComics(8);
  const tags = tagOptions.filter((tag) => tag !== "全部");

  return (
    <SiteLayout>
      <PageMeta
        title="JMComic漫画频道 - 精选漫画阅读平台"
        description="JMComic为漫画爱好者提供丰富的漫画分类和最新连载，涵盖多种风格与题材，用户可方便浏览和追踪精彩漫画作品。"
        keywords="JMComic,漫画,漫画阅读,连载漫画,漫画平台,漫画分类,漫画推荐"
        path="/"
      />

      <div className="pt-6 md:pt-8">
        <HeroCarousel />
      </div>

      <div className="mt-10">
        <CategoryQuickLinks />
      </div>

      <div className="mx-auto mt-12 w-full max-w-[1240px] px-4 md:px-6">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <SectionHeading
              eyebrow="Recently Updated"
              title="最新连载漫画"
              description="按更新时间排序，第一时间看到喜欢作品的新章节。"
              action={
                <Link
                  to="/updates"
                  className="flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-700 hover:text-primary-800"
                >
                  全部更新
                  <i className="ri-arrow-right-line"></i>
                </Link>
              }
            />
            <div className="space-y-3">
              {latest.map((comic) => (
                <ComicRow key={comic.id} comic={comic} />
              ))}
            </div>
          </div>

          <aside className="space-y-4 lg:col-span-4">
            <section className="rounded-lg border border-background-200 bg-background-50 p-4">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="flex items-center gap-1.5 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-layout-grid-line text-lg text-primary-500"></i>
                  分类快捷入口
                </h3>
                <Link
                  to="/category/all"
                  className="whitespace-nowrap text-[11px] font-medium text-primary-700 hover:text-primary-800"
                >
                  全部
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                {allCategories.map((category) => (
                  <Link
                    key={category.id}
                    to={`/category/${category.id}`}
                    className="flex items-center gap-1.5 rounded-md border border-background-200 px-2 py-1.5 text-[11px] font-medium text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    <i className={`${category.icon} text-sm text-primary-600`}></i>
                    {category.name}
                  </Link>
                ))}
              </div>
            </section>

            <HotRanking />

            <section className="rounded-lg border border-background-200 bg-background-50 p-4">
              <h3 className="flex items-center gap-1.5 font-heading text-base font-bold text-foreground-950">
                <i className="ri-price-tag-3-line text-lg text-accent-600"></i>
                热门题材标签
              </h3>
              <p className="mt-1 text-[11px] text-foreground-500">点击标签直接搜索同题材作品</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/search?q=${encodeURIComponent(tag)}`}
                    className="rounded-full border border-background-300 bg-background-50 px-2.5 py-1 text-[11px] font-medium text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    {tag}
                  </Link>
                ))}
              </div>
            </section>

            <section className="rounded-lg border border-primary-200 bg-primary-50 p-4">
              <h3 className="font-heading text-base font-bold text-primary-900">不知道看什么？</h3>
              <p className="mt-1.5 text-xs leading-relaxed text-primary-800/80">
                从 {allComics.length} 部精选作品中随机逛一逛，按地区、状态与题材组合筛选，很快就能找到合胃口的作品。
              </p>
              <Link
                to="/category/all"
                className="mt-3 flex h-10 items-center justify-center gap-1.5 rounded-md bg-primary-500 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-compass-3-line"></i>
                开始探索
              </Link>
            </section>
          </aside>
        </div>
      </div>

      <div className="mx-auto mt-14 w-full max-w-[1240px] px-4 md:px-6">
        <SectionHeading
          eyebrow="Editor's Picks"
          title="热门漫画推荐"
          description="综合阅读量与收藏量挑选的高人气作品，口碑与热度兼具。"
          action={
            <Link
              to="/category/all"
              className="flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-700 hover:text-primary-800"
            >
              更多推荐
              <i className="ri-arrow-right-line"></i>
            </Link>
          }
        />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
          {popular.map((comic, index) => (
            <ComicCard key={comic.id} comic={comic} rank={index + 1} />
          ))}
        </div>
      </div>

      <div className="mx-auto mt-14 w-full max-w-[1240px] px-4 md:px-6">
        <div className="grid grid-cols-1 gap-4 rounded-lg border border-background-200 bg-background-50 p-5 md:grid-cols-3 md:p-8">
          <div className="md:col-span-2">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              每天十分钟，跟进你追的连载
            </h2>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
              JMComic 把「分类浏览」和「连载更新」放在同一个入口：分类页支持题材、地区、状态与排序组合筛选，
              更新页每天汇总最新章节，不用在多个站点之间来回跳转。
            </p>
            <div className="mt-5 flex flex-wrap gap-2.5">
              <Link
                to="/updates"
                className="flex h-10 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-flashlight-line"></i>
                查看今日更新
              </Link>
              <Link
                to="/about"
                className="flex h-10 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                了解 JMComic
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 md:grid-cols-1 md:gap-2">
            {[
              { label: "精选作品", value: "20+", icon: "ri-book-marked-line" },
              { label: "题材分类", value: "8", icon: "ri-layout-grid-line" },
              { label: "连载追踪", value: "每日", icon: "ri-calendar-check-line" },
            ].map((stat) => (
              <div
                key={stat.label}
                className="flex items-center gap-2.5 rounded-md border border-background-200 bg-background-50 px-3 py-2.5"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded bg-accent-100 text-accent-700">
                  <i className={`${stat.icon} text-base`}></i>
                </span>
                <span>
                  <span className="block font-heading text-sm font-bold text-foreground-950">
                    {stat.value}
                  </span>
                  <span className="block text-[10px] text-foreground-500">{stat.label}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}